import { addDays, differenceInDays, endOfDay, format } from "date-fns";
import * as Yup from "yup";
import { validarCuilCuit } from "../../../../util/cuilcuit";
import { dateIsBefore, isDecimal as hasTwoDecimals } from "../../../../util/validacionesYup";
import {
  validacionCalle,
  validacionCodigoPostal,
  validacionDepartamento,
  validacionKm,
  validacionLocalidad,
  validacionNumeroCalle,
  validacionPais,
  validacionPiso,
  validacionProvincia,
} from "../../../commons/yup/ValidacionesLocalidades";
import { enumTiposGestion } from "../commons/enumTipoGestion";

Yup.addMethod(Yup.BaseSchema, 'requiredOrEmpty', function (required, notRequiredErrorMessage, requiredErrorMessage = 'El campo es requerido') {
  return this.test('required-if-present', function (value) {
    const { path, parent, createError } = this;

    if (required && value === null) {
      return createError({ message: requiredErrorMessage });
    }
    if (!required && value !== null) {
      return createError({ message: notRequiredErrorMessage })
    }
    return true;
  });
});

const cuitRegexGuiones = /^(20|23|24|27|30|33|34)-\d{8}-\d$/;
const cuitRegexSinGuiones = /^(20|23|24|27|30|33|34)\d{8}\d$/;

const isEmpty = (value) => value === null || value === undefined || value === "";

const isNumeroAlturaEspecial = (numero) => numero === "1";

const tieneLatLong = (latitud, longitud) => !isEmpty(latitud) && !isEmpty(longitud);

const tieneEntreCalles = (entreCalle, entreCalle1) => !isEmpty(entreCalle) && !isEmpty(entreCalle1);

const validarCampoObligatorioSiKm = (value, km, createError) => {
  if (km && isEmpty(value)) {
    return createError({ message: "Campo obligatorio" });
  }
  return null;
};

const validarLatLongOEntreCalles = (value, parent, createError) => {
  const { numero, entreCalle, entreCalle1, latitud, longitud } = parent;

  if (!isNumeroAlturaEspecial(numero)) return null;

  const tienePosicion = tieneLatLong(latitud, longitud);
  const tieneCalles = tieneEntreCalles(entreCalle, entreCalle1);

  if (!tienePosicion && !tieneCalles && isEmpty(value)) {
    return createError({ message: "Debe completar Entre Calles o Latitud/Longitud" });
  }

  return null;
};

export const validateNewEstablecimiento = (fechaInicioObra = null) => Yup.object().shape({
  nombre: Yup.string()
    .required("El nombre del establecimiento es obligatorio")
    .max(30, "Máximo 30 caracteres"),
  fechaInicioActividad: Yup.date()
    .nullable()
    .required("Campo obligatorio")
    .typeError("Fecha inválida")
    .max(
      fechaInicioObra || addDays(endOfDay(new Date()), -1),
      fechaInicioObra
        ? "La fecha de inicio de actividad debe ser menor o igual a la fecha de inicio de obra"
        : "La fecha de inicio de actividad no puede ser igual ni posterior a la fecha actual"
    )
    .typeError("Fecha inválida"),
  actividad: Yup.string().required("Campo obligatorio").nullable(),
  esTercero: Yup.boolean(),
  cuit: Yup.string()
    .test({
      name: "formatoCUIT",
      message:
        "CUIT inválido. Debe tener el formato XX-XXXXXXXXX-X o XXXXXXXXXXX",
      test: function (value) {
        if (!value) return true;
        // Validar si el CUIT tiene guiones o no
        if (value.includes("-")) {
          return cuitRegexGuiones.test(value); // Validar con guiones
        } else {
          return cuitRegexSinGuiones.test(value); // Validar sin guiones
        }
      },
    })
    .test({
      name: "cuitValido",
      message: "Debe ingresar un CUIT válido",
      test: function (value) {
        if (value) {
          // Remover los guiones si están presentes antes de validar
          const cuitSinGuiones = value.replace(/-/g, "");
          return validarCuilCuit(cuitSinGuiones, true, true);
        }
        return true;
      },
    })
    .when("esTercero", {
      is: true,
      then: Yup.string().required("Campo obligatorio"),
    }),
  km: validacionKm,

  latitud: Yup.number()
    .test({
      name: "latitudValida",
      message: "Debe ingresar una latitud válida entre -90 y 90 grados",
      test: function (value) {
        if (!isEmpty(value)) {
          const isNumber = !isNaN(value);
          const isWithinRange = value >= -90 && value <= 90;
          return isNumber && isWithinRange;
        }
        return true;
      },
    })
    .typeError("La latitud debe ser un numero")
    .test({
      name: "latitudObligatoria",
      test: function (value) {
        const { km } = this.parent;

        const errorKm = validarCampoObligatorioSiKm(value, km, this.createError);
        if (errorKm) return errorKm;

        const errorLatLong = validarLatLongOEntreCalles(value, this.parent, this.createError);
        if (errorLatLong) return errorLatLong;

        return true;
      },
    }),

  longitud: Yup.number()
    .test({
      name: "longitudValida",
      message: "Debe ingresar una longitud válida entre -180 y 180 grados",
      test: function (value) {
        if (!isEmpty(value)) {
          const isNumber = !isNaN(value);
          const isWithinRange = value >= -180 && value <= 180;
          return isNumber && isWithinRange;
        }
        return true;
      },
    })
    .typeError("La longitud debe ser un numero")
    .test({
      name: "longitudObligatoria",
      test: function (value) {
        const { km, numero, entreCalle, entreCalle1, latitud } = this.parent;

        const errorKm = validarCampoObligatorioSiKm(value, km, this.createError);
        if (errorKm) return errorKm;

        if (isNumeroAlturaEspecial(numero)) {
          const tienePosicion = tieneLatLong(latitud, value);
          const tieneCalles = tieneEntreCalles(entreCalle, entreCalle1);

          if (!tienePosicion && !tieneCalles) {
            return this.createError({ message: "Debe completar Entre Calles o Latitud/Longitud" });
          }
        }

        return true;
      },
    }),
  piso: validacionPiso,
  departamento: validacionDepartamento,
  pais: validacionPais.required("Campo obligatorio"),
  provincia: validacionProvincia.required("Campo obligatorio"),
  localidad: validacionLocalidad.required("Campo obligatorio"),
  calle: validacionCalle.required("Campo obligatorio"),
  codigoPostal: validacionCodigoPostal.required("Campo obligatorio"),
  entreCalle: validacionCalle.test({
    name: "entreCalleObligatoria",
    test: function (value) {
      const { numero, latitud, longitud } = this.parent;

      if (!isNumeroAlturaEspecial(numero)) return true;

      const tienePosicion = tieneLatLong(latitud, longitud);

      if (!tienePosicion && isEmpty(value)) {
        return this.createError({ message: "Debe completar Entre Calles o Latitud/Longitud" });
      }

      return true;
    },
  }),
  entreCalle1: validacionCalle.test({
    name: "entreCalle1Obligatoria",
    test: function (value) {
      const { numero, latitud, longitud, entreCalle } = this.parent;

      if (!isNumeroAlturaEspecial(numero)) return true;
      if (isEmpty(entreCalle)) return true;

      const tienePosicion = tieneLatLong(latitud, longitud);

      if (!tienePosicion && isEmpty(value)) {
        return this.createError({ message: "Debe completar Y Calle" });
      }

      return true;
    },
  }),
  numero: validacionNumeroCalle("km").test({
    name: "numeroNoCero",
    message: "El número ingresado es inválido",
    test: function (value) {
      if (!value) {
        return true;
      }

      const valorNumerico = parseFloat(String(value).replace(",", "."));
      return !Number.isNaN(valorNumerico) && valorNumerico > 0;
    },
  }),
});


export const identificacionFormValidation = (
  lastFormProps,
  obtenerNombreTipoGestion,
  informadoSRT,
  diasFechaInicioObra,
  esObraPrexistente,
  habilitadoGuardadoObrasConIntercambiosPendientes = null
) => {
  return (
    Yup.object().shape({
      idTipoAvisoObra: Yup.string().nullable().when([], {
        is: () => lastFormProps?.estado,
        then: Yup.string().nullable().required("Campo obligatorio").test(({
          name: "No gestiona si no esta informado",
          message: "No se puede realizar la gestión hasta tanto no esté informada la suspensión a la SRT",
          test: (value) => {
            if (
              value &&
              habilitadoGuardadoObrasConIntercambiosPendientes == "0"
            ) {
              return informadoSRT;
            }
            return true;
          }
        }))
      }),
      denominacionObra: Yup.string()
        .required("Campo obligatorio")
        .min(10, "Mínimo 10 caracteres")
        .max(255, "Máximo 255 caracteres"),
      fechaInicioObra: Yup.date()
        .nullable()
        .required("Campo obligatorio")
        .typeError("Fecha inválida")
        .test({
          name: "fechaLimiteVerificacion",
          message:
            "La fecha de inicio de actividad debe ser menor o igual a la fecha de fin de estimada",
          test: (value, testContext) => {
            if (value && testContext.parent.fechaFinObra) {
              return dateIsBefore(value, testContext.parent.fechaFinObra);
            }

            return true;
          },
        })
        .test({
          name: "limite x dias parametro ml",
          message: `La fecha de inicio de actividad debe ser hasta ${diasFechaInicioObra} días antes`,
          test: (value, testContext) => {
            if (value && diasFechaInicioObra !== null && diasFechaInicioObra >= 0 && !esObraPrexistente) {
              return dateIsBefore(addDays(new Date(), -diasFechaInicioObra), value);
            }
            return true;
          }
        })
        .test({
          name: "limite7DiasRes",
          message:
            "La duración de la obra esta limitada a 7 dias",
          test: (value, testContext) => {
            if (value && testContext.parent.fechaFinObra && testContext.parent.resolucion === '319/99') {
              return Math.abs(differenceInDays(value, testContext.parent.fechaFinObra)) < 7;
            }

            return true;
          },
        }),

      fechaFinObra: Yup.date()
        .nullable()
        .required("Campo obligatorio")
        .typeError("Fecha inválida")
        .test({
          name: "fechaLimiteVerificacion",
          message:
            "La fecha de finalizacion estimada debe ser posterior o igual a la fecha de inicio de actividad",
          test: (value, testContext) => {
            if (!value || !testContext.parent.fechaInicioObra) {
              return true;
            }

            return dateIsBefore(testContext.parent.fechaInicioObra, value);
          },
        })
        .test({
          name: "valorMinimoFecha",
          message:
            "La fecha de finalización no puede ser anterior a la fecha de presentación",
          test: function (value, testContext) {
            if (!value) {
              return true;
            }

            if (obtenerNombreTipoGestion(testContext.parent.idTipoAvisoObra) === enumTiposGestion.extension) {
              return true;
            }

            const fechaReferencia = !esObraPrexistente
              ? new Date()
              : testContext.parent.fechaRecepcion;

            // Normalizamos ambas fechas al inicio del día para comparar solo fecha calendario y permitir igualdad
            const fechaFin = new Date(value);
            fechaFin.setHours(0, 0, 0, 0);

            const fechaRef = new Date(fechaReferencia);
            fechaRef.setHours(0, 0, 0, 0);

            return fechaFin >= fechaRef;
          },
        })
        .test({
          name: "limite7DiasRes",
          message:
            "La duración de la obra esta limitada a 7 dias",
          test: (value, testContext) => {
            if (value && testContext.parent.fechaInicioObra && testContext.parent.resolucion === '319/99') {
              return Math.abs(differenceInDays(testContext.parent.fechaInicioObra, value)) < 7;
            }

            return true;
          },
        }),
      resolucion: Yup.string().required("Campo obligatorio"),
      idTipoContratista: Yup.string(),
      superficieAConstruir: Yup.string()
        .matches(/^[0-9]+(,[0-9]+)?$/, "Solo ingrese comas y números")
        .test({
          name: "cantidadDigitosDecimales",
          message: "Máximo 2 dígitos después de la coma",
          test: (value) => hasTwoDecimals(value),
        })
        .test({
          name: "cantidadTotalDigitos",
          message: "Máximo 8 dígitos enteros",
          test: function (value) {
            if (value) {
              return value?.split(",")[0]?.length <= 8;
            } else {
              return true;
            }
          },
        }),
      numeroPlantas: Yup.number()
        .min(0, "No se permiten números negativos")
        .integer("No ingresar caracteres")
        .max(2147483647, "No puede ingresar números mayores a 2.147.483.647")
        .nullable(),
      fechaHastaExtensionObra: Yup.mixed().when("idTipoAvisoObra", {
        is: (idTipoAvisoObra) => {
          return obtenerNombreTipoGestion(idTipoAvisoObra) === enumTiposGestion.extension
        },
        then: Yup.date().required("Campo Obligatorio").nullable().typeError("Fecha inválida")
          .test({
            name: "fechaMinimaExtension",
            message:
              "La Fecha de Extensión debe ser posterior a la Fecha de Fin de Obra",
            test: function (value, testContext) {
              if (value && testContext.parent.fechaFinObra) {
                return dateIsBefore(
                  addDays(testContext.parent.fechaFinObra, 1),
                  value
                );
              }
              return true;
            }
          })
          .test({
            name: "extension despues de otra",
            message: "La Fecha de Extensión debe ser posterior a la anterior Fecha de Extensión",
            test: function (value, testContext) {
              if (value && lastFormProps.ultimaFechaExtension) {
                return dateIsBefore(
                  // Si es edicion puede tener misma fecha extension, caso contrario debe ser al menos un dia mas
                  addDays(
                    lastFormProps.ultimaFechaExtension,
                    obtenerNombreTipoGestion(testContext.parent.idTipoAvisoObra, true) !== enumTiposGestion.edicion
                      ? 1
                      : 0
                  ), value);
              }
              return true;
            }
          })

      }),
      fechaSuspension: Yup.mixed().when("idTipoAvisoObra", {
        is: (idTipoAvisoObra) => obtenerNombreTipoGestion(idTipoAvisoObra) === enumTiposGestion.suspension || obtenerNombreTipoGestion(idTipoAvisoObra) === enumTiposGestion.reinicio,
        then: Yup.date()
          .required("Campo Obligatorio")
          .nullable()
          .typeError("Fecha inválida")
          .test({
            message: "La Fecha de Suspensión debe ser posterior a la Fecha de Inicio de Actividad",
            name: "fecha inicio de obra",
            test: function (value, testContext) {
              if (value && testContext.parent.fechaInicioObra) {
                return dateIsBefore(testContext.parent.fechaInicioObra, value);
              }
              return true;
            }
          })
          .test({
            message: "La Fecha de Suspensión no puede coincidir con otro periodo de Suspensión",
            name: "no deben coincidir periodos de suspensión",
            test: function (value, testContext) {
              if (value && obtenerNombreTipoGestion(testContext.parent.idTipoAvisoObra, true) !== enumTiposGestion.edicion) {
                const periodo = lastFormProps.periodosSuspension.find((periodoSuspension) => dateIsBefore(periodoSuspension.fechaSuspension, value) && dateIsBefore(value, periodoSuspension.fechaReinicio));
                if (periodo !== undefined) {
                  return testContext.createError({ message: `La Fecha de Suspensión coincide con el periodo de suspensión existente entre las fechas ${format(periodo.fechaSuspension, "dd/MM/yyyy")} y ${format(periodo.fechaReinicio, "dd/MM/yyyy")}` })
                }
              }
              return true;
            }
          })

      }),
      fechaFinReinicioSuspension: Yup.mixed()
        .when("idTipoAvisoObra", {
          is: (idTipoAvisoObra) => obtenerNombreTipoGestion(idTipoAvisoObra) === enumTiposGestion.suspension,
          then: Yup.date()
            .nullable()
            .typeError("Fecha inválida")
            .test({
              name: "fechaMinimaExtension",
              message:
                "La Fecha de Fin de Suspensión debe ser posterior a la Fecha de Inicio de Suspensión",
              test: function (value, testContext) {
                if (value && testContext.parent.fechaSuspension) {
                  return dateIsBefore(
                    testContext.parent.fechaSuspension,
                    value
                  );
                }
                return true;
              },
            })
            .test({
              message: "La Fecha de Fin de Suspensión debe ser anterior a la Fecha de Fin de Obra",
              name: "fechaFinDeObra",
              test: function (value, testContext) {
                if (value && testContext.parent.fechaFinObra && !testContext.parent.fechaHastaExtensionObra) {
                  return dateIsBefore(value, testContext.parent.fechaFinObra);
                }
                return true;
              }
            })
            .test({
              message: "La Fecha de Fin de Suspensión debe ser anterior a la Fecha de Extensión",
              name: "fechaExtension",
              test: function (value, testContext) {
                if (value && testContext.parent.fechaHastaExtensionObra) {
                  return dateIsBefore(value, testContext.parent.fechaHastaExtensionObra);
                }
                return true;
              }
            })
            .test({
              message: "La Fecha de Fin de Suspensión no puede coincidir con otro periodo de Suspensión",
              name: "no deben coincidir periodos de suspensión",
              test: function (value, testContext) {
                if (value && obtenerNombreTipoGestion(testContext.parent.idTipoAvisoObra, true) !== enumTiposGestion.edicion) {
                  const periodo = lastFormProps.periodosSuspension.find((periodoSuspension) => dateIsBefore(periodoSuspension.fechaSuspension, value) && dateIsBefore(value, periodoSuspension.fechaReinicio));
                  if (periodo !== undefined) {
                    return testContext.createError({ message: `La Fecha de Suspensión coincide con el periodo de suspensión existente entre las fechas ${format(periodo.fechaSuspension, "dd/MM/yyyy")} y ${format(periodo.fechaReinicio, "dd/MM/yyyy")}` })
                  }
                }
                return true;
              }
            })
        }),
      fechaReinicioSuspension: Yup.mixed().when("idTipoAvisoObra", {
        is: (idTipoAvisoObra) => obtenerNombreTipoGestion(idTipoAvisoObra) === enumTiposGestion.reinicio,
        then: Yup.date().required("Campo Obligatorio").nullable().typeError("Fecha inválida")
          .test({
            message: "La Fecha de Reinicio debe ser posterior a la Fecha de Inicio de Suspensión",
            name: "fechaInicioSupension",
            test: function (value, testContext) {
              if (value && testContext.parent.fechaSuspension) {
                return dateIsBefore(testContext.parent.fechaSuspension, value);
              }
              return true;
            }
          })
          .test({
            message: "La Fecha de Reinicio debe ser anterior a la Fecha de Fin de Suspensión",
            name: "fechaFinSuspension",
            test: function (value, testContext) {
              if (value && testContext.parent.fechaFinReinicioSuspension) {
                return dateIsBefore(value, testContext.parent.fechaFinReinicioSuspension);
              }
              return true;
            }
          })
          .test({
            message: "La Fecha de Reinicio debe ser anterior a la Fecha de Fin de Obra",
            name: "fechaFinDeObra",
            test: function (value, testContext) {
              if (value && testContext.parent.fechaFinObra && !testContext.parent.fechaHastaExtensionObra) {
                return dateIsBefore(value, testContext.parent.fechaFinObra);
              }
              return true;
            }
          })
          .test({
            message: "La Fecha de Reinicio debe ser anterior a la Fecha de Extensión",
            name: "fechaExtension",
            test: function (value, testContext) {
              if (value && testContext.parent.fechaHastaExtensionObra) {
                return dateIsBefore(value, testContext.parent.fechaHastaExtensionObra);
              }
              return true;
            }
          })
      }),
    }))
};

export const ubicacionFormValidation = Yup.object().shape({
  idDependenciaEmpleador: Yup.string().nullable().required("Campo obligatorio"),
});



const notRequiredErrorMessage = (item) => {
  return `Al tener marcarda la actividad '${item.pregunta}' como 'No' no debe completar este campo`;
}
const requiredErrorMessage = (item) => {
  return `Al tener marcada la actividad '${item.pregunta}' como 'Si' se debe completar este campo`
}

/***
Las Fecha Inicio: between Fecha Inicio de obra y fin de obra

La fecha fin: between Fecha Inicio (demolición o excavación según corresponda) y fin de obra

se puede dar que el inicio y fin sean iguales

No se puede enviar una fecha fin sin fecha de inciio
*/
const validateInitialDemolitionDate = (itemsActividades, secuencia) => {
  const itemActividad = itemsActividades.find((item) => item.secuencia === secuencia);
  let schema = Yup.date().nullable();

  if (secuencia != -1) {
    schema = schema.requiredOrEmpty(
      itemActividad?.resultado === "1",
      notRequiredErrorMessage(itemActividad),
      requiredErrorMessage(itemActividad)
    )
  }
  schema = schema.typeError("Fecha inválida")
    .test({
      name: "fechaMinimaVerificacion",
      message:
        "La Fecha de Inicio debe estar entre la Fecha de Inicio de obra y la Fecha Fin de obra",
      test: function (value, testContext) {
        if (value) {
          return dateIsBefore(
            testContext.from[1].value.identificacion.fechaInicioObra,
            value
          );
        }
        return true;
      },
    })
    .test({
      name: "fechaMaximaVerificacion",
      message:
        "La Fecha Inicio debe ser menor o igual a la Fecha de Fin de obra",
      test: (value, testContext) => {
        if (value && !testContext.from[1].value.identificacion.fechaHastaExtensionObra) {
          return dateIsBefore(
            value,
            testContext.from[1].value.identificacion.fechaFinObra
          );
        }
        return true;
      }
    })
    .test({
      name: "fechaMaximaExtension",
      message: "La Fecha Inicio debe ser menor o igual a la Fecha de Extensión",
      test: function (value, testContext) {
        if (value && testContext.from[1].value.identificacion.fechaHastaExtensionObra) {
          return dateIsBefore(value, testContext.from[1].value.identificacion.fechaHastaExtensionObra);
        }
        return true;
      }
    });
  return schema;
};

const validateEndDemolitionDate = (fechaInicio, nombreGestionObra) => {
  return Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .when(`${fechaInicio}`, {
      is: (f) => {
        return f !== null;
      },
      then: Yup.date()
        .nullable()
        .required("Fecha de Fin es requerida si existe Fecha de Inicio")
        .typeError("Fecha inválida")
        .test({
          name: "fechaMinimaVerificacion",
          message: "La Fecha de Fin debe ser mayor a la Fecha de Inicio",
          test: (value, testContext) => {
            if (value) {
              return dateIsBefore(testContext.parent[fechaInicio], value);
            }
            return true;
          },
        })
        .test({
          name: "fechaMaximaVerificacion",
          message:
            "La Fecha Fin debe ser menor o igual a la Fecha de Fin de obra",
          test: function (value, testContext) {
            if (value && !testContext.from[1].value.identificacion.fechaHastaExtensionObra) {
              return dateIsBefore(
                value,
                testContext.from[1].value.identificacion.fechaFinObra
              );
            }
            return true;
          },
        })
        .test({
          name: "fechaMaximaExtension",
          message: "La Fecha Fin debe ser menor o igual a la Fecha de Extensión",
          test: function (value, testContext) {
            if (value && testContext.from[1].value.identificacion.fechaHastaExtensionObra) {
              return dateIsBefore(value, testContext.from[1].value.identificacion.fechaHastaExtensionObra);
            }
            return true;
          }
        })
    });
};

export const demolitionFormValidation = (nombreGestionObra, itemsActividades) => {
  return Yup.object().shape({
    demolicion: Yup.object().shape({
      fechaInicioDemolicion: validateInitialDemolitionDate(itemsActividades, 13),
      fechaFinDemolicion: validateEndDemolitionDate("fechaInicioDemolicion", nombreGestionObra),
      fechaInicioExcavacion: validateInitialDemolitionDate(itemsActividades, 14),
      fechaFinExcavacion: validateEndDemolitionDate("fechaInicioExcavacion", nombreGestionObra),
      fechaInicioExcavacionCA: validateInitialDemolitionDate(itemsActividades, -1),
      fechaFinExcavacionCA: validateEndDemolitionDate("fechaInicioExcavacionCA", nombreGestionObra),
      excavacionCAOtros: Yup.string()
        .max(100, "Máximo 100 caracteres")
        .test({
          name: "fechaInicioExcavacionCA",
          message: "Al cargar una fecha de inicio de excavación a cielo abierto este campo es obligatorio",
          test: function (value, testContext) {
            if (!value && testContext.from[0].value.fechaInicioExcavacionCA) {
              return false;
            }
            return true;
          },
        })
        .test({
          name: "debeContenerCaracteres",
          message: "Debe contener caracteres",
          test: function (value, testContext) {
            if (value?.trim().length === 0) {
              return false;
            }
            return true;
          },
        })
        .nullable(),
      observaciones: Yup.string()
        .min(30, "Mínimo 30 caracteres")
        .max(2000, "Máximo 2000 caracteres")
        .test({
          name: "debeContenerCaracteres",
          message: "Debe contener caracteres",
          test: function (value, testContext) {
            if (value?.trim().length === 0) {
              return false;
            }
            return true;
          },
        })
        .nullable()

    }),
  });
}


export const validateActividadADesarrollar = Yup.object({
  otrasActividades: Yup.boolean().test({
    name: "resultadoDebeSerSiSiHayTextoExtra",
    message: "Si detalla otras actividades debe marcar Sí",
    test: function (value, context) {
      const { parent } = context;
      if (parent.detalleOtrasActividades?.trim().length > 0 && value !== true) {
        return false;
      }
      return true;
    },
  }),
  detalleOtrasActividades: Yup.string()
    .max(100, "El detalle de otras actividades no puede superar los 100 caracteres")
    .nullable()
    .test({
      name: "detalleOtrasActividadesRequerido",
      message: "Debe detallar otras actividades cuando marca Sí",
      test: function (value, context) {
        const { parent } = context;
        if (parent?.otrasActividades === true) {
          return value != null && String(value).trim().length > 0;
        }
        return true;
      },
    }),
  opciones: Yup.array().of(
    Yup.object().shape({
      resultado: Yup.string()
        .oneOf(["1", "0"]),
      seleccionOpcion: Yup.string()
        .nullable()
        .test({
          name: "seleccionOpcionEsObligatorio",
          message: "Campo obligatorio",
          test: function (value, context) {
            const { parent } = context;
            if (
              parent.resultado === "1" &&
              parent.opciones?.length > 0 &&
              !parent.seleccionOpcion
            ) {
              return false;
            }
            return true;
          },
        }),
    })
  ),
});
